/**
 * In-thread find: search loaded conversation messages (text + attached
 * file names) and jump helpers used by ChatView / SearchModal.
 */

export function messageIdsEqual(message, targetId) {
  if (!message || targetId == null || targetId === '') return false;
  const target = String(targetId);
  if (message.id != null && String(message.id) === target) return true;
  if (message._id != null && String(message._id) === target) return true;
  // Locally-created bubbles keep their original client id here after the
  // server renames `id`, so a turn can still be located mid-round-trip.
  if (message._stableKey != null && String(message._stableKey) === target) return true;
  return false;
}

/**
 * The DOM id suffix `MessageHighlightWrapper` renders for the list item that
 * holds `targetMessageId` (a group is addressed by its first member).
 */
export function findMessageDomId(items, targetMessageId) {
  const index = findGroupedMessageIndex(items, targetMessageId);
  if (index < 0) return null;
  const item = items[index];
  const owner = item?.type === 'group' ? item.messages?.[0] : item;
  const domId = owner?.id ?? owner?._id;
  return domId == null ? null : String(domId);
}

export function getMessageSearchableText(message) {
  if (!message) return '';
  const parts = [];
  if (typeof message.content === 'string' && message.content) {
    parts.push(message.content);
  }
  if (typeof message.thinkingContent === 'string' && message.thinkingContent) {
    parts.push(message.thinkingContent);
  }
  const files = Array.isArray(message.files) ? message.files : [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const name = file?.name || file?.originalName;
    if (typeof name === 'string' && name) parts.push(name);
  }
  return parts.join('\n');
}

export function findConversationMatches(messages, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle || !Array.isArray(messages) || messages.length === 0) return [];

  const matches = [];
  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    if (!message || message.isHidden) continue;
    const id = message.id || message._id;
    if (!id) continue;

    const text = getMessageSearchableText(message);
    if (!text || !text.toLowerCase().includes(needle)) continue;

    const content = typeof message.content === 'string' ? message.content : '';
    const matchedFileOnly = content.toLowerCase().indexOf(needle) === -1
      && (message.files || []).some((file) => {
        const name = `${file?.name || ''} ${file?.originalName || ''}`.toLowerCase();
        return name.includes(needle);
      });

    matches.push({
      messageId: String(id),
      isArchivedMessage: message.isArchivedMessage === true,
      archiveOriginalIndex: Number.isFinite(message.archive?.originalIndex)
        ? message.archive.originalIndex
        : (Number.isFinite(message.originalIndex) ? message.originalIndex : null),
      kind: matchedFileOnly ? 'file' : 'text',
    });
  }
  return matches;
}

export function findGroupedMessageIndex(items, targetMessageId) {
  if (!Array.isArray(items) || targetMessageId == null || targetMessageId === '') return -1;
  return items.findIndex((item) => {
    if (item?.type === 'group') {
      return (item.messages || []).some((m) => messageIdsEqual(m, targetMessageId));
    }
    return messageIdsEqual(item, targetMessageId);
  });
}

export function scrollMessageNodeIntoView(messageId, { animated = false } = {}) {
  if (typeof document === 'undefined' || !messageId) return false;
  const el = document.getElementById(`msg-hl-${messageId}`);
  if (!el || typeof el.scrollIntoView !== 'function') return false;
  try {
    el.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: animated ? 'smooth' : 'auto',
    });
    return true;
  } catch (_) {
    return false;
  }
}

/** Center the highlighted phrase inside the chat, even in a very tall answer.
 * Return false until the highlight has rendered so the owning scroll request
 * can retry. Never scroll the page or another conversation's list.
 */
export function scrollSearchMatchIntoView(messageId, scroller, { query, animated = false } = {}) {
  if (typeof document === 'undefined' || !messageId || !scroller?.isConnected) return false;
  const needle = String(query || '').trim().toLowerCase();
  if (!needle || scroller.clientHeight <= 0) return false;
  const message = document.getElementById(`msg-hl-${messageId}`);
  if (!message || !scroller.contains(message)) return false;
  const mark = Array.from(message.querySelectorAll('mark.search-hl')).find(node => (
    node.textContent.toLowerCase() === needle && node.getClientRects().length > 0
  ));
  if (!mark) return false;
  const nativeScrollTo = scroller.ownerDocument.defaultView.Element.prototype.scrollTo;
  const scrollTo = (node, top, behavior) => {
    if (typeof nativeScrollTo === 'function') nativeScrollTo.call(node, { top, behavior });
    else node.scrollTop = top;
  };
  // A mark can have client rects while clipped by the thinking panel's own
  // viewport. Reveal inner scroll containers before measuring the outer list,
  // and never traverse past this conversation's scroller.
  for (let node = mark.parentElement; node && node !== scroller; node = node.parentElement) {
    if (node.clientHeight <= 0 || node.scrollHeight <= node.clientHeight) continue;
    const overflow = scroller.ownerDocument.defaultView.getComputedStyle(node).overflowY;
    if (!/^(auto|scroll)$/.test(overflow)) continue;
    const markRect = mark.getBoundingClientRect();
    const top = Math.max(0, Math.min(node.scrollHeight - node.clientHeight,
      node.scrollTop + markRect.top - node.getBoundingClientRect().top - node.clientTop
      - Math.max(0, (node.clientHeight - markRect.height) / 2)));
    if (Number.isFinite(top) && Math.abs(node.scrollTop - top) > 1) scrollTo(node, top, 'auto');
  }
  const rect = mark.getBoundingClientRect();
  const containerTop = scroller.getBoundingClientRect().top + scroller.clientTop;
  const inset = Math.max(0, (scroller.clientHeight - rect.height) / 2);
  const top = Math.max(0, Math.min(
    scroller.scrollHeight - scroller.clientHeight,
    scroller.scrollTop + rect.top - containerTop - inset,
  ));
  if (!Number.isFinite(top)) return false;
  if (Math.abs(scroller.scrollTop - top) > 1) {
    // RN Web replaces this node's scrollTo with an {x, y, animated} API.
    // Invoke the DOM implementation explicitly so {top} cannot become y=0.
    scrollTo(scroller, top, animated ? 'smooth' : 'auto');
  }
  return true;
}

/**
 * Put the top of a message at the top of `scroller` so a long streaming
 * reply can grow downward in place. `scrollIntoView({ block: 'start' })`
 * is not used: it can scroll the window instead of the chat list.
 */
export function scrollMessageNodeToStart(messageId, scroller, { padding = 12 } = {}) {
  if (typeof document === 'undefined' || !messageId || !scroller) return false;
  const el = document.getElementById(`msg-hl-${messageId}`);
  if (!el) return false;
  try {
    const next = scroller.scrollTop
      + (el.getBoundingClientRect().top - scroller.getBoundingClientRect().top)
      - padding;
    scroller.scrollTop = Math.max(0, next);
    return true;
  } catch (_) {
    return false;
  }
}

export function isFindInChatKeyEvent(event) {
  if (!event || event.altKey || event.shiftKey) return false;
  if (!(event.metaKey || event.ctrlKey)) return false;
  return String(event.key || '').toLowerCase() === 'f';
}

export function isFindNextKeyEvent(event) {
  if (!event) return false;
  const key = String(event.key || '');
  if (key === 'F3') return true;
  if (!(event.metaKey || event.ctrlKey) || event.altKey) return false;
  return key.toLowerCase() === 'g';
}

/**
 * Find-in-chat submits on Enter, not on each keystroke. A new draft commits
 * the query; repeating Enter on the same query advances to the next match.
 */
export function resolveConversationFindSubmit({ draftQuery, committedQuery, shiftKey = false } = {}) {
  const draft = String(draftQuery || '').trim();
  const committed = String(committedQuery || '').trim();
  if (draft !== committed) {
    return { action: 'commit', query: draft };
  }
  return { action: 'advance', delta: shiftKey ? -1 : 1 };
}

/** The document / spreadsheet / presentation editors ship their own find bar
 *  on Cmd+F. Their handlers run on bubble, so a capturing window listener that
 *  swallows the event would silently replace them with the chat find. */
export function shouldDeferFindShortcut(event) {
  const target = event?.target;
  if (!target || typeof target.closest !== 'function') return false;
  if (target.closest('.gab-editor-root')) return true;
  if (target.closest('.gab-document-editor')) return true;
  return false;
}
